import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { teamAPI, rosterAPI } from '../services/api';
import { adminAPI } from '../services/adminAPI';
import Footer from '../components/Footer';
import Papa from 'papaparse';
import * as XLSX from 'xlsx';
import Navbar from '../components/Navbar';
import { getLogoSrc } from '../utils/logoHelper';
import { useBackgroundRemoval } from '../hooks/useBackgroundRemoval';
import api from '../services/api';
import './CreateTeam.css';

const API_BASE_URL = api.defaults.baseURL.replace('/api', '');

function EditTeam() {
  const navigate = useNavigate();
  const { id } = useParams();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState('');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [teamLimitInfo, setTeamLimitInfo] = useState(null);
  const [isOrgTeam, setIsOrgTeam] = useState(false);
  const [isTeamOwner, setIsTeamOwner] = useState(false);
  const [orgTemplateSettings, setOrgTemplateSettings] = useState(null);
  const [orgLogoUrl, setOrgLogoUrl] = useState(null);
  const [previousLogoUrl, setPreviousLogoUrl] = useState(null);
  const [originalColors, setOriginalColors] = useState(null);
  const [savedColors, setSavedColors] = useState(null);
  const [colorsChanged, setColorsChanged] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    sport: '',
    primaryColor: '#003366',
    secondaryColor: '#FFFFFF',
    season: '',
    ageGroup: '',
  });
  const [seasons, setSeasons] = useState([]);
  const [ageGroups, setAgeGroups] = useState([]);
  // Logo state
  const [currentLogoUrl, setCurrentLogoUrl] = useState(null);
  // eslint-disable-next-line
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [originalLogoImage, setOriginalLogoImage] = useState(null);
  const [cutoutLogoImage, setCutoutLogoImage] = useState(null);
  const [activeLogoVersion, setActiveLogoVersion] = useState('original');

  const { removeBg: removeLogoBg, isRemoving: isRemovingLogo, bgRemovalError: logoRemovalError } = useBackgroundRemoval();

  const [removeLogo, setRemoveLogo] = useState(false);

  // Roster state
  const [roster, setRoster] = useState([]);
  const [rosterExpanded, setRosterExpanded] = useState(
    window.location.hash === '#roster'
  );
  const [addingPlayer, setAddingPlayer] = useState(false);
  const [editingPlayerId, setEditingPlayerId] = useState(null);
  const [showDeletePlayerConfirm, setShowDeletePlayerConfirm] = useState(null);
  const [newPlayer, setNewPlayer] = useState({
    name: '',
    number: '',
    position: '',
    dob: '',
  });
  const [editPlayer, setEditPlayer] = useState({
    name: '',
    number: '',
    position: '',
    dob: '',
  });

  // CSV Import state
  const [showCSVModal, setShowCSVModal] = useState(false);
  const [csvFile, setCsvFile] = useState(null);
  const [csvData, setCsvData] = useState([]);
  const [csvHeaders, setCsvHeaders] = useState([]);
  const [csvMapping, setCsvMapping] = useState({
    name: '',
    number: '',
    position: '',
    dob: '',
  });
  const [importing, setImporting] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [toastMessage, setToastMessage] = useState({ type: '', text: '' });
  // Helper function to show toast messages
  const showToast = (type, text, duration = 3000) => {
    setToastMessage({ type, text });
    setTimeout(() => setToastMessage({ type: '', text: '' }), duration);
  };

  useEffect(() => {
    if (window.location.hash === '#roster') {
      setRosterExpanded(true);
    }
  }, []);

  // Separate effect to scroll after data loads
  useEffect(() => {
    if (!loading && rosterExpanded && window.location.hash === '#roster') {
      setTimeout(() => {
        const rosterSection = document.querySelector('.roster-section');
        if (rosterSection) {
          rosterSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 100);
    }
  }, [loading, rosterExpanded]);

  // Load team data, subscription info, and roster when page loads
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [teamResponse, limitInfoResponse, rosterResponse] =
          await Promise.all([
            teamAPI.getById(id),
            teamAPI.getLimitInfo(),
            rosterAPI.getTeamRoster(id),
          ]);

        const team = teamResponse.data;
        setTeamLimitInfo(limitInfoResponse.data);
        // Sort roster by number (players without numbers go to end)
        const sortedRoster = [...rosterResponse.data].sort((a, b) => {
          // Handle empty numbers
          if (!a.number && !b.number) return 0;
          if (!a.number) return 1;
          if (!b.number) return -1;

          // Convert to integers for proper numeric sorting
          const numA = parseInt(a.number, 10);
          const numB = parseInt(b.number, 10);

          return numA - numB;
        });
        setRoster(sortedRoster);
        setIsOrgTeam(team.organizationTeam === true);
        setIsTeamOwner(team.isTeamOwner === true);
        setOrgTemplateSettings(team.orgTemplateSettings || null);
        setOrgLogoUrl(team.orgLogoUrl || null);
        // Use org defaults as fallback for display
        const displayPrimaryColor =
          team.primaryColor || team.orgPrimaryColor || '#003366';
        const displaySecondaryColor =
          team.secondaryColor || team.orgSecondaryColor || '#FFFFFF';

        setFormData({
          name: team.name,
          sport: team.sport,
          primaryColor: displayPrimaryColor,
          secondaryColor: displaySecondaryColor,
          season: team.season,
          ageGroup: team.ageGroup,
        });
        setOriginalColors({
          primary: team.primaryColor, // Keep null if team doesn't have custom colors
          secondary: team.secondaryColor,
        });
        setSavedColors({
          primary: displayPrimaryColor, // Display color (with fallback)
          secondary: displaySecondaryColor,
        });

        const logoToUse = team.logoUrl || (team.organizationTeam ? team.orgLogoUrl : null);
        if (logoToUse) {
          setCurrentLogoUrl(logoToUse);
          setOriginalLogoImage(logoToUse);
        }

        setLoading(false);
      } catch (err) {
        console.error('EditTeam fetch error:', err);
        console.error('Error response:', err.response?.data);
        setError(err.response?.data?.error || 'Failed to load team');
        setLoading(false);
      }
    };

    fetchData();
  }, [id]);

  useEffect(() => {
    fetchActiveSeasons();
    fetchActiveAgeGroups();
  }, []);

  const fetchActiveSeasons = async () => {
    try {
      const response = await adminAPI.getActiveSeasons();
      setSeasons(response.data);
    } catch (error) {
      console.error('Error fetching seasons:', error);
    }
  };

  const fetchActiveAgeGroups = async () => {
    try {
      const response = await adminAPI.getActiveAgeGroups();
      setAgeGroups(response.data);
    } catch (error) {
      console.error('Error fetching age groups:', error);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });

    // Track if colors have changed
    if ((name === 'primaryColor' || name === 'secondaryColor') && savedColors) {
      const newPrimary =
        name === 'primaryColor' ? value : formData.primaryColor;
      const newSecondary =
        name === 'secondaryColor' ? value : formData.secondaryColor;
      const hasChanged =
        newPrimary !== savedColors.primary ||
        newSecondary !== savedColors.secondary;
      setColorsChanged(hasChanged);
    }
  };

  // Handle logo file selection
  const handleLogoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        setError('Logo file must be less than 5MB');
        return;
      }

      const allowedTypes = [
        'image/jpeg',
        'image/jpg',
        'image/png',
        'image/gif',
        'image/svg+xml',
      ];
      if (!allowedTypes.includes(file.type)) {
        setError('Logo must be an image file (JPEG, PNG, GIF, or SVG)');
        return;
      }

      setLogoFile(file);
      setRemoveLogo(false);
      setError('');

      const reader = new FileReader();
      reader.onloadend = () => {
        setLogoPreview(reader.result);
        setOriginalLogoImage(reader.result);
        setCutoutLogoImage(null);
        setActiveLogoVersion('original');
      };
      reader.readAsDataURL(file);
    }
  };

  const handleRemoveLogo = () => {
    if (isOrgTeam && orgLogoUrl) {
      // For org teams, revert to org default logo
      setPreviousLogoUrl(currentLogoUrl);
      setLogoPreview(orgLogoUrl);
      setLogoFile(null);
      setCurrentLogoUrl(null);
      setRemoveLogo(false);
    } else {
      // For personal teams, remove logo completely
      setLogoFile(null);
      setLogoPreview(null);
      setCurrentLogoUrl(null);
      setRemoveLogo(true);
    }
  };

  const handleCancelNewLogo = () => {
    setLogoFile(null);
    setLogoPreview(null);
    if (previousLogoUrl) {
      setCurrentLogoUrl(previousLogoUrl);
      setPreviousLogoUrl(null);
    }
  };

  const handleRemoveLogoBg = async () => {
    if (!originalLogoImage) return;
    const result = await removeLogoBg(originalLogoImage);
    if (result) {
      const { blobUrl, toBase64 } = result;
      setCutoutLogoImage(blobUrl);
      setLogoPreview(blobUrl);
      setActiveLogoVersion('cutout');
      toBase64.then((base64) => {
        setCutoutLogoImage(base64);
        setLogoPreview(base64);
      });
    }
  };

  const handleToggleLogoVersion = (version) => {
    setActiveLogoVersion(version);
    setLogoPreview(version === 'cutout' ? cutoutLogoImage : originalLogoImage);
  };
  // eslint-disable-next-line
  const handleRevertColors = async () => {
    if (!originalColors) return;

    setSaving(true);
    setError('');

    try {
      await teamAPI.update(id, {
        primaryColor: originalColors.primary,
        secondaryColor: originalColors.secondary,
      });

      showToast('success', 'Colors reverted to team defaults!');

      // Update form and saved colors
      setFormData({
        ...formData,
        primaryColor: originalColors.primary,
        secondaryColor: originalColors.secondary,
      });
      setSavedColors({
        primary: originalColors.primary,
        secondary: originalColors.secondary,
      });
      setColorsChanged(false);
    } catch (err) {
      if (err.response?.status === 403) {
        setError('You do not have permission to update team colors.');
      } else {
        setError(err.response?.data?.error || 'Failed to revert colors');
      }
    } finally {
      setSaving(false);
    }
  };

  const handleSaveColorsOnly = async () => {
    if (!colorsChanged) return;

    setSaving(true);
    setError('');

    try {
      await teamAPI.update(id, {
        primaryColor: formData.primaryColor,
        secondaryColor: formData.secondaryColor,
      });

      showToast('success', 'Colors saved successfully!');

      // Update original colors to new saved values (non-null means custom colors are saved)
      setOriginalColors({
        primary: formData.primaryColor,
        secondary: formData.secondaryColor,
      });
      setSavedColors({
        primary: formData.primaryColor,
        secondary: formData.secondaryColor,
      });
      setColorsChanged(false);
    } catch (err) {
      if (err.response?.status === 403) {
        setError('You do not have permission to update team colors.');
      } else {
        setError(err.response?.data?.error || 'Failed to save colors');
      }
    } finally {
      setSaving(false);
    }
  };

  // Roster Management Functions
  const isValidDate = (dateString) => {
    if (!dateString || dateString.trim() === '') return true; // Empty is OK (optional field)

    // Strict format check: MM/DD/YYYY (1-2 digits for month/day, exactly 4 for year)
    const datePattern = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/;
    const match = dateString.match(datePattern);

    if (!match) return false;

    const month = parseInt(match[1], 10);
    const day = parseInt(match[2], 10);
    const year = parseInt(match[3], 10);

    // Validate ranges
    if (month < 1 || month > 12) return false;
    if (day < 1 || day > 31) return false;
    if (year < 1950 || year > new Date().getFullYear() + 10) return false;

    // Create date and verify it's actually valid (catches things like Feb 30)
    const date = new Date(year, month - 1, day);
    if (
      date.getMonth() !== month - 1 ||
      date.getDate() !== day ||
      date.getFullYear() !== year
    ) {
      return false;
    }

    return true;
  };

  const handleAddPlayer = async () => {
    if (!newPlayer.name.trim()) {
      setError('Player name is required');
      return;
    }

    if (newPlayer.dob && !isValidDate(newPlayer.dob)) {
      setError('Invalid date format. Use MM/DD/YYYY');
      return;
    }

    try {
      const response = await rosterAPI.createPlayer({
        teamId: id,
        ...newPlayer,
      });
      setRoster([...roster, response.data]);
      setNewPlayer({ name: '', number: '', position: '', dob: '' });
      setAddingPlayer(false);
      setError('');
    } catch (err) {
      setError('Failed to add player');
    }
  };

  const handleEditPlayer = async (playerId) => {
    if (!editPlayer.name.trim()) {
      setError('Player name is required');
      return;
    }

    if (editPlayer.dob && !isValidDate(editPlayer.dob)) {
      setError('Invalid date format. Use MM/DD/YYYY');
      return;
    }
    try {
      await rosterAPI.updatePlayer(playerId, editPlayer);

      // Refresh roster from server to get latest data
      const response = await rosterAPI.getTeamRoster(id);
      setRoster(response.data);

      setEditingPlayerId(null);
      setError('');
    } catch (err) {
      setError('Failed to update player');
    }
  };

  const handleDeletePlayer = async (playerId) => {
    try {
      await rosterAPI.deletePlayer(playerId);
      setRoster(roster.filter((p) => p.id !== playerId));
      setShowDeletePlayerConfirm(null);
    } catch (err) {
      setError('Failed to delete player');
    }
  };

  const startEditingPlayer = (player) => {
    setEditingPlayerId(player.id);

    // Convert DOB from YYYY-MM-DD to MM/DD/YYYY for editing
    let formattedDob = '';
    if (player.dob) {
      const [year, month, day] = player.dob.split('-');
      formattedDob = `${month}/${day}/${year}`;
    }
    setEditPlayer({
      name: player.name,
      number: player.number || '',
      position: player.position || '',
      dob: formattedDob || '',
    });
  };

  // CSV/XLSX Import Functions
  // Drag and drop handlers
  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      const file = e.dataTransfer.files[0];
      // Process the dropped file
      const event = { target: { files: [file] } };
      handleFileUpload(event);
    }
  };

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setCsvFile(file);

    const fileExtension = file.name.split('.').pop().toLowerCase();

    if (fileExtension === 'csv') {
      // Handle CSV with Papa Parse
      Papa.parse(file, {
        header: true,
        skipEmptyLines: true,
        complete: (results) => {
          setCsvHeaders(results.meta.fields);
          setCsvData(results.data);
        },
        error: (error) => {
          setError('Failed to parse CSV file');
          console.error(error);
        },
      });
    } else if (['xlsx', 'xls'].includes(fileExtension)) {
      // Handle Excel with XLSX
      const reader = new FileReader();
      reader.onload = (event) => {
        try {
          const data = new Uint8Array(event.target.result);
          const workbook = XLSX.read(data, { type: 'array', cellDates: true }); // Add cellDates: true

          // Get first sheet
          const firstSheetName = workbook.SheetNames[0];
          const worksheet = workbook.Sheets[firstSheetName];

          // Convert to JSON with date formatting
          const jsonData = XLSX.utils.sheet_to_json(worksheet, {
            header: 1,
            raw: false, // Don't use raw values, format them
            dateNF: 'yyyy-mm-dd', // Format dates as YYYY-MM-DD
          });

          if (jsonData.length < 2) {
            setError(
              'Excel file must have at least a header row and one data row'
            );
            return;
          }

          // First row is headers
          const headers = jsonData[0].map((h) => String(h).trim());
          setCsvHeaders(headers);

          // Convert remaining rows to objects
          const dataRows = jsonData
            .slice(1)
            .map((row) => {
              const obj = {};
              headers.forEach((header, index) => {
                let value =
                  row[index] !== undefined ? String(row[index]).trim() : '';

                // Additional date parsing for common date formats
                if (value && !isNaN(value) && Number(value) > 1000) {
                  // Might be an Excel serial date number
                  const excelDate = XLSX.SSF.parse_date_code(Number(value));
                  if (excelDate) {
                    value = `${excelDate.y}-${String(excelDate.m).padStart(
                      2,
                      '0'
                    )}-${String(excelDate.d).padStart(2, '0')}`;
                  }
                }

                obj[header] = value;
              });
              return obj;
            })
            .filter((row) => {
              // Filter out empty rows
              return Object.values(row).some((val) => val !== '');
            });

          setCsvData(dataRows);
        } catch (error) {
          setError('Failed to parse Excel file');
          console.error(error);
        }
      };
      reader.readAsArrayBuffer(file);
    } else {
      setError('Please upload a CSV or Excel file (.csv, .xlsx, .xls)');
      setCsvFile(null);
    }
  };

  const handleCSVImport = async () => {
    if (!csvMapping.name) {
      setError('Please map the Name field');
      return;
    }

    setImporting(true);

    try {
      const players = csvData
        .map((row) => ({
          name: row[csvMapping.name],
          number: csvMapping.number ? row[csvMapping.number] : '',
          position: csvMapping.position ? row[csvMapping.position] : '',
          dob: csvMapping.dob ? row[csvMapping.dob] : '',
        }))
        .filter((player) => player.name && player.name.trim() !== ''); // Only import rows with names

      const response = await rosterAPI.bulkImport({
        teamId: id,
        players,
      });

      setRoster([...roster, ...response.data.players]);
      setShowCSVModal(false);
      setCsvFile(null);
      setCsvData([]);
      setCsvHeaders([]);
      setCsvMapping({ name: '', number: '', position: '', dob: '' });
      setError('');
    } catch (err) {
      setError('Failed to import players');
    } finally {
      setImporting(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSaving(true);

    try {
      // Create JSON payload instead of FormData
      const submitData = {
        name: formData.name,
        sport: formData.sport,
        primaryColor: formData.primaryColor,
        secondaryColor: formData.secondaryColor,
        season: formData.season,
        ageGroup: formData.ageGroup,
      };

      // Handle logo changes
      if (logoPreview) {
        submitData.logoUrl = logoPreview;
      } else if (cutoutLogoImage && activeLogoVersion === 'cutout') {
        submitData.logoUrl = cutoutLogoImage;
      } else if (removeLogo) {
        // User wants to remove logo
        submitData.removeLogo = 'true';
      }
      // Otherwise, don't send logoUrl (keeps existing logo)

      await teamAPI.update(id, submitData);
      navigate(`/team/${id}`);
    } catch (err) {
      if (
        err.response?.status === 403 &&
        err.response?.data?.error === 'Logo upload restricted'
      ) {
        showToast(
          'error',
          `${err.response.data.message}. Redirecting to upgrade options...`,
          3000
        );
        setTimeout(() => navigate('/membership'), 2000);
        navigate('/membership');
      } else {
        setError(err.response?.data?.error || 'Failed to update team');
      }
    } finally {
      setSaving(false);
    }
  };

  const handleSaveLogoOnly = async () => {
    if (!logoPreview) return;

    setSaving(true);
    setError('');

    try {
      await teamAPI.update(id, {
        logoUrl: logoPreview,
      });

      showToast('success', 'Logo saved successfully!');

      // Refresh team data
      const teamResponse = await teamAPI.getById(id);
      setCurrentLogoUrl(teamResponse.data.logoUrl);
      setLogoPreview(null);
      setLogoFile(null);
    } catch (err) {
      if (err.response?.status === 403) {
        setError('You do not have permission to update this team logo.');
      } else {
        setError(err.response?.data?.error || 'Failed to save logo');
      }
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await teamAPI.delete(id);
      navigate('/');
    } catch (err) {
      setError('Failed to delete team');
      setDeleting(false);
    }
  };

  const isPremiumUser =
    teamLimitInfo && teamLimitInfo.subscriptionPlan !== 'free';
  const isFreeUser = teamLimitInfo && teamLimitInfo.subscriptionPlan === 'free';

  if (loading) {
    return (
      <div className="create-team-container">
        <div className="create-team-content">
          <div className="loading-state">Loading team...</div>
        </div>
      </div>
    );
  }

  return (
    <>
      <Navbar />
      <div className="create-team-container">
        <div className="create-team-content">
          <div className="form-header">
            <button
              onClick={() => navigate(`/team/${id}`)}
              className="back-button"
            >
              ← Back to Team Portal
            </button>
            <h1>Edit Team</h1>
            <p>Update your team profile and colors</p>
          </div>

          {error && <div className="error-message">{error}</div>}

          <form onSubmit={handleSubmit} className="team-form">
            {/* Logo Upload/Edit Section - EXISTING CODE UNCHANGED */}
            {isPremiumUser || isOrgTeam ? (
              <div className="form-group logo-upload-section">
                <label>Team Logo</label>
                <div
                  className="logo-upload-container"
                  style={
                    !isTeamOwner &&
                    !(isOrgTeam && orgTemplateSettings?.allowLogoUpload)
                      ? { pointerEvents: 'none' }
                      : {}
                  }
                >
                  {logoPreview ? (
                    <div className="logo-preview">
                      <img src={logoPreview} alt="New logo preview" />
                      {isRemovingLogo && (
                        <div style={{
                          position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.6)',
                          display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', borderRadius: '8px',
                        }}>
                          <div style={{
                            width: '28px', height: '28px',
                            border: '3px solid rgba(255,255,255,0.3)',
                            borderTopColor: '#fff', borderRadius: '50%',
                            animation: 'bg-removal-spin 0.75s linear infinite',
                          }} />
                          <p style={{ color: '#fff', fontSize: '12px', margin: '8px 0 0' }}>Removing…</p>
                        </div>
                      )}
                      <div className="logo-actions">
                        <button
                          type="button"
                          className="btn-remove-logo"
                          onClick={handleCancelNewLogo}
                          disabled={saving || deleting || isRemovingLogo ||
                            (!isTeamOwner && !(isOrgTeam && orgTemplateSettings?.allowLogoUpload))
                          }
                        >
                          ✕ Cancel New Logo
                        </button>
                      </div>
                      <button
                        type="button"
                        className="btn-secondary"
                        onClick={handleRemoveLogoBg}
                        disabled={isRemovingLogo || saving || deleting}
                        style={{ marginTop: '8px', width: '100%' }}
                      >
                        {isRemovingLogo ? 'Removing background…' : '✂️ Remove Background'}
                      </button>
                      {logoRemovalError && (
                        <p style={{ color: '#e53e3e', fontSize: '12px', marginTop: '4px' }}>{logoRemovalError}</p>
                      )}
                      {originalLogoImage && cutoutLogoImage && (
                        <div style={{ display: 'flex', gap: '8px', marginTop: '8px' }}>
                          <button
                            type="button"
                            className={activeLogoVersion === 'original' ? 'btn-primary' : 'btn-secondary'}
                            onClick={() => handleToggleLogoVersion('original')}
                            style={{ flex: 1 }}
                            disabled={isRemovingLogo}
                          >
                            Original
                          </button>
                          <button
                            type="button"
                            className={activeLogoVersion === 'cutout' ? 'btn-primary' : 'btn-secondary'}
                            onClick={() => handleToggleLogoVersion('cutout')}
                            style={{ flex: 1 }}
                            disabled={isRemovingLogo}
                          >
                            ✂️ Cutout
                          </button>
                        </div>
                      )}
                    </div>
                  ) : removeLogo ? (
                    <div className="logo-removed-state">
                      <span className="removed-icon">🚫</span>
                      <p className="removed-text">
                        Logo will be removed when you save
                      </p>
                      <button
                        type="button"
                        className="btn btn-primary"
                        onClick={() => {
                          setRemoveLogo(false);
                          const fetchTeam = async () => {
                            const response = await teamAPI.getById(id);
                            if (response.data.logoUrl) {
                              setCurrentLogoUrl(response.data.logoUrl);
                            }
                          };
                          fetchTeam();
                        }}
                        disabled={
                          saving ||
                          deleting ||
                          (!isTeamOwner &&
                            !(
                              isOrgTeam && orgTemplateSettings?.allowLogoUpload
                            ))
                        }
                      >
                        ↶ Undo Remove
                      </button>
                    </div>
                  ) : currentLogoUrl ? (
                    <div className="logo-preview">
                      <img
                        src={cutoutLogoImage && activeLogoVersion === 'cutout' ? cutoutLogoImage : getLogoSrc(currentLogoUrl, API_BASE_URL)}
                        alt="Current team logo"
                      />
                      {isRemovingLogo && (
                        <div style={{
                          position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.6)',
                          display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', borderRadius: '8px',
                        }}>
                          <div style={{
                            width: '28px', height: '28px',
                            border: '3px solid rgba(255,255,255,0.3)',
                            borderTopColor: '#fff', borderRadius: '50%',
                            animation: 'bg-removal-spin 0.75s linear infinite',
                          }} />
                          <p style={{ color: '#fff', fontSize: '12px', margin: '8px 0 0' }}>Removing…</p>
                        </div>
                      )}
                      <div className="logo-actions">
                        <label
                          htmlFor="logo-upload-replace"
                          className="btn-change-logo"
                        >
                          📸 Change Logo
                        </label>
                        <button
                          type="button"
                          className="btn-remove-logo"
                          onClick={handleRemoveLogo}
                          disabled={
                            saving || deleting || isRemovingLogo ||
                            (!isTeamOwner && !(isOrgTeam && orgTemplateSettings?.allowLogoUpload))
                          }
                        >
                          ✕ Remove Logo
                        </button>
                      </div>
                      <button
                        type="button"
                        className="btn-secondary"
                        onClick={handleRemoveLogoBg}
                        disabled={isRemovingLogo || saving || deleting}
                        style={{ marginTop: '8px', width: '100%' }}
                      >
                        {isRemovingLogo ? 'Removing background…' : '✂️ Remove Background'}
                      </button>
                      {logoRemovalError && (
                        <p style={{ color: '#e53e3e', fontSize: '12px', marginTop: '4px' }}>{logoRemovalError}</p>
                      )}
                      {cutoutLogoImage && (
                        <div style={{ display: 'flex', gap: '8px', marginTop: '8px' }}>
                          <button
                            type="button"
                            className={activeLogoVersion === 'original' ? 'btn-primary' : 'btn-secondary'}
                            onClick={() => handleToggleLogoVersion('original')}
                            style={{ flex: 1 }}
                            disabled={isRemovingLogo}
                          >
                            Original
                          </button>
                          <button
                            type="button"
                            className={activeLogoVersion === 'cutout' ? 'btn-primary' : 'btn-secondary'}
                            onClick={() => handleToggleLogoVersion('cutout')}
                            style={{ flex: 1 }}
                            disabled={isRemovingLogo}
                          >
                            ✂️ Cutout
                          </button>
                        </div>
                      )}
                    </div>
                  ) : (
                    <label
                      htmlFor="logo-upload"
                      className="logo-upload-placeholder"
                    >
                      <span className="upload-icon">📸</span>
                      <p>Upload Team Logo</p>
                      <p className="upload-hint">
                        PNG, JPG, GIF or SVG (Max 5MB)
                      </p>
                    </label>
                  )}
                  <input
                    id="logo-upload"
                    type="file"
                    accept="image/*"
                    onChange={handleLogoChange}
                    className="logo-file-input"
                    disabled={
                      saving ||
                      deleting ||
                      (!isTeamOwner &&
                        !(isOrgTeam && orgTemplateSettings?.allowLogoUpload))
                    }
                  />
                  <input
                    id="logo-upload-replace"
                    type="file"
                    accept="image/*"
                    onChange={handleLogoChange}
                    className="logo-file-input"
                    disabled={
                      saving ||
                      deleting ||
                      (!isTeamOwner &&
                        !(isOrgTeam && orgTemplateSettings?.allowLogoUpload))
                    }
                  />
                </div>

                {!isTeamOwner &&
                  isOrgTeam &&
                  orgTemplateSettings?.allowLogoUpload &&
                  logoPreview && (
                    <div style={{ marginTop: '16px', textAlign: 'center' }}>
                      <button
                        type="button"
                        className="btn btn-primary"
                        onClick={handleSaveLogoOnly}
                        disabled={saving}
                      >
                        {saving ? 'Saving Logo...' : '💾 Save Logo'}
                      </button>
                    </div>
                  )}
              </div>
            ) : !isOrgTeam ? (
              <div className="form-group logo-upload-section">
                <label>Team Logo</label>
                {currentLogoUrl ? (
                  <div className="logo-preview-locked">
                    <img
                      src={getLogoSrc(currentLogoUrl, API_BASE_URL)}
                      alt="Current team logo"
                    />
                    <div className="logo-locked-message">
                      <span className="lock-icon">🔒</span>
                      <p>Logo changes available on Pro and Premium plans</p>
                      <button
                        type="button"
                        className="btn-upgrade-small"
                        onClick={() => navigate('/membership')}
                      >
                        ⭐ Upgrade to Edit Logo
                      </button>
                    </div>
                  </div>
                ) : (
                  <div
                    className="logo-upgrade-message"
                    onClick={() => navigate('/membership')}
                    style={{ cursor: 'pointer' }}
                  >
                    <span className="upgrade-icon">⭐</span>
                    <div>
                      <p className="upgrade-title">Upgrade to Add Team Logos</p>
                      <p className="upgrade-subtitle">
                        Logo uploads are available on Pro and Premium plans
                      </p>
                    </div>
                  </div>
                )}
              </div>
            ) : null}

            {/* Team Info Fields - EXISTING CODE UNCHANGED */}
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="name">Team Name *</label>
                <input
                  id="name"
                  name="name"
                  type="text"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="Aledo Tigers"
                  required
                  disabled={saving || deleting || !isTeamOwner}
                  maxLength={25}
                />
                <div className="char-counter">
                  {formData.name.length}/25 characters
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="sport">Sport *</label>
                <select
                  id="sport"
                  name="sport"
                  value={formData.sport}
                  onChange={handleChange}
                  required
                  disabled={saving || deleting || !isTeamOwner}
                >
                  <option value="">Select sport</option>
                  <option value="Baseball">Baseball</option>
                  <option value="Softball">Softball</option>
                </select>
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="ageGroup">Age Group *</label>
                <select
                  id="ageGroup"
                  name="ageGroup"
                  value={formData.ageGroup}
                  onChange={handleChange}
                  required
                  disabled={saving || deleting || !isTeamOwner}
                >
                  <option value="">Select age group</option>
                  {ageGroups.map((ageGroup) => (
                    <option key={ageGroup.id} value={ageGroup.name}>
                      {ageGroup.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="season">Season *</label>
                <select
                  id="season"
                  name="season"
                  value={formData.season}
                  onChange={handleChange}
                  required
                  disabled={saving || deleting || !isTeamOwner}
                >
                  <option value="">Select Season</option>
                  {seasons.map((season) => (
                    <option key={season.id} value={season.name}>
                      {season.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="primaryColor">Primary Color</label>
                <div className="color-input-wrapper">
                  <input
                    id="primaryColor"
                    name="primaryColor"
                    type="color"
                    value={formData.primaryColor}
                    onChange={handleChange}
                    disabled={
                      saving ||
                      deleting ||
                      (!isTeamOwner &&
                        !(isOrgTeam && orgTemplateSettings?.allowColorChange))
                    }
                  />
                  <input
                    type="text"
                    value={formData.primaryColor}
                    onChange={(e) => {
                      const newValue = e.target.value;
                      setFormData({ ...formData, primaryColor: newValue });
                      if (savedColors) {
                        const hasChanged =
                          newValue !== savedColors.primary ||
                          formData.secondaryColor !== savedColors.secondary;
                        setColorsChanged(hasChanged);
                      }
                    }}
                    placeholder="#003366"
                    className="color-text-input"
                    disabled={
                      saving ||
                      deleting ||
                      (!isTeamOwner &&
                        !(isOrgTeam && orgTemplateSettings?.allowColorChange))
                    }
                  />
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="secondaryColor">Secondary Color</label>
                <div className="color-input-wrapper">
                  <input
                    id="secondaryColor"
                    name="secondaryColor"
                    type="color"
                    value={formData.secondaryColor}
                    onChange={handleChange}
                    disabled={
                      saving ||
                      deleting ||
                      (!isTeamOwner &&
                        !(isOrgTeam && orgTemplateSettings?.allowColorChange))
                    }
                  />
                  <input
                    type="text"
                    value={formData.secondaryColor}
                    onChange={(e) => {
                      const newValue = e.target.value;
                      setFormData({
                        ...formData,
                        secondaryColor: newValue,
                      });
                      if (savedColors) {
                        const hasChanged =
                          formData.primaryColor !== savedColors.primary ||
                          newValue !== savedColors.secondary;
                        setColorsChanged(hasChanged);
                      }
                    }}
                    placeholder="#FFFFFF"
                    className="color-text-input"
                    disabled={
                      saving ||
                      deleting ||
                      (!isTeamOwner &&
                        !(isOrgTeam && orgTemplateSettings?.allowColorChange))
                    }
                  />
                </div>
              </div>
            </div>
            <div className="color-preview">
              <div className="preview-label">Color Preview:</div>
              <div
                className="preview-swatch"
                style={{
                  backgroundColor: formData.primaryColor,
                  border: `4px solid ${formData.secondaryColor}`,
                }}
              >
                <span style={{ color: formData.secondaryColor }}>
                  {formData.name || 'Team Name'}
                </span>
              </div>
            </div>

            {/* Color Reset Buttons - Only for org teams */}
            {isOrgTeam && orgTemplateSettings?.allowColorChange && (
              <div
                style={{
                  marginTop: '16px',
                  textAlign: 'center',
                  display: 'flex',
                  gap: '12px',
                  justifyContent: 'center',
                  flexWrap: 'wrap',
                }}
              >
                {/* Reset to Org Defaults - Available to both owners and coaches */}
                {/* Show if team has custom colors saved (originalColors are not null) */}
                {(originalColors?.primary !== null ||
                  originalColors?.secondary !== null) && (
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={async () => {
                      setSaving(true);
                      setError('');
                      try {
                        await teamAPI.update(id, {
                          primaryColor: null,
                          secondaryColor: null,
                        });
                        showToast(
                          'success',
                          'Colors reset to organization defaults!'
                        );

                        // Reload page to refresh with org defaults
                        window.location.reload();
                      } catch (err) {
                        setError(
                          err.response?.data?.error || 'Failed to reset colors'
                        );
                      } finally {
                        setSaving(false);
                      }
                    }}
                    disabled={saving || deleting}
                  >
                    🏢 Reset to Organization Default Colors
                  </button>
                )}
              </div>
            )}

            {/* Save Colors Button - Only for coaches when colors changed */}
            {!isTeamOwner &&
              isOrgTeam &&
              orgTemplateSettings?.allowColorChange &&
              colorsChanged && (
                <div style={{ marginTop: '16px', textAlign: 'center' }}>
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={handleSaveColorsOnly}
                    disabled={saving}
                  >
                    {saving ? 'Saving Colors...' : '💾 Save Colors'}
                  </button>
                </div>
              )}

            {/* NEW: ROSTER SECTION */}
            <div className="roster-section">
              <div
                className="roster-header"
                onClick={() =>
                  (isOrgTeam || !isFreeUser) &&
                  setRosterExpanded(!rosterExpanded)
                }
                style={{
                  cursor: isOrgTeam || !isFreeUser ? 'default' : 'pointer',
                }}
              >
                <h3>👥 Team Roster</h3>
                {(isOrgTeam || !isFreeUser) && (
                  <span className="expand-icon">
                    {rosterExpanded ? '▼' : '▶'}
                  </span>
                )}
              </div>

              {!isOrgTeam && isFreeUser ? (
                <div
                  className="roster-upgrade-message"
                  onClick={() => navigate('/membership')}
                >
                  <span className="upgrade-icon">⭐</span>
                  <div>
                    <p className="upgrade-title">
                      Upgrade to Pro to Build Your Team Roster
                    </p>
                    <p className="upgrade-subtitle">
                      Add players and select them for game recap graphics
                    </p>
                  </div>
                </div>
              ) : (
                rosterExpanded && (
                  <div className="roster-content">
                    {error && <div className="error-message">{error}</div>}
                    <div className="roster-actions">
                      <button
                        type="button"
                        className="btn-add-player"
                        onClick={() => setAddingPlayer(true)}
                        disabled={addingPlayer}
                      >
                        + Add Player
                      </button>
                      <button
                        type="button"
                        className="btn-import-csv"
                        onClick={() => setShowCSVModal(true)}
                      >
                        📄 Import CSV
                      </button>
                    </div>

                    {roster.length === 0 && !addingPlayer ? (
                      <div className="roster-empty">
                        <p>
                          No players on roster yet. Add players to select them
                          in game recap graphics.
                        </p>
                      </div>
                    ) : (
                      <div className="roster-table-container">
                        <table className="roster-table">
                          <thead>
                            <tr>
                              <th>Number</th>
                              <th>Name</th>
                              <th>Position</th>
                              <th>DOB</th>
                              <th>Actions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {addingPlayer && (
                              <tr className="player-form-row">
                                <td>
                                  <input
                                    type="text"
                                    placeholder="#"
                                    value={newPlayer.number}
                                    onChange={(e) =>
                                      setNewPlayer({
                                        ...newPlayer,
                                        number: e.target.value,
                                      })
                                    }
                                    className="player-input"
                                    onKeyDown={(e) => {
                                      if (e.key === 'Enter') {
                                        e.preventDefault();
                                        handleAddPlayer();
                                      }
                                    }}
                                  />
                                </td>
                                <td>
                                  <input
                                    type="text"
                                    placeholder="Name *"
                                    value={newPlayer.name}
                                    onChange={(e) =>
                                      setNewPlayer({
                                        ...newPlayer,
                                        name: e.target.value,
                                      })
                                    }
                                    className="player-input"
                                    required
                                    onKeyDown={(e) => {
                                      if (e.key === 'Enter') {
                                        e.preventDefault();
                                        handleAddPlayer();
                                      }
                                    }}
                                  />
                                </td>
                                <td>
                                  <input
                                    type="text"
                                    placeholder="Pos"
                                    value={newPlayer.position}
                                    onChange={(e) =>
                                      setNewPlayer({
                                        ...newPlayer,
                                        position: e.target.value,
                                      })
                                    }
                                    className="player-input"
                                    onKeyDown={(e) => {
                                      if (e.key === 'Enter') {
                                        e.preventDefault();
                                        handleAddPlayer();
                                      }
                                    }}
                                  />
                                </td>
                                <td>
                                  <input
                                    type="text"
                                    value={newPlayer.dob}
                                    onChange={(e) =>
                                      setNewPlayer({
                                        ...newPlayer,
                                        dob: e.target.value,
                                      })
                                    }
                                    placeholder="MM/DD/YYYY"
                                    className="player-input"
                                    onKeyDown={(e) => {
                                      if (e.key === 'Enter') {
                                        e.preventDefault();
                                        handleAddPlayer();
                                      }
                                    }}
                                    style={{
                                      borderColor:
                                        newPlayer.dob &&
                                        new Date(newPlayer.dob).toString() ===
                                          'Invalid Date'
                                          ? '#ef4444'
                                          : '',
                                    }}
                                  />
                                  {newPlayer.dob &&
                                    (isValidDate(newPlayer.dob) ? (
                                      <div
                                        style={{
                                          fontSize: '10px',
                                          color: '#10b981',
                                          marginTop: '2px',
                                        }}
                                      >
                                        ✓{' '}
                                        {new Date(
                                          newPlayer.dob
                                        ).toLocaleDateString()}
                                      </div>
                                    ) : (
                                      <div
                                        style={{
                                          fontSize: '10px',
                                          color: '#ef4444',
                                          marginTop: '2px',
                                        }}
                                      >
                                        ✗ Invalid date - use MM/DD/YYYY
                                      </div>
                                    ))}
                                </td>
                                <td>
                                  <button
                                    type="button"
                                    onClick={handleAddPlayer}
                                    className="btn-save-player"
                                  >
                                    ✓
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => {
                                      setAddingPlayer(false);
                                      setNewPlayer({
                                        name: '',
                                        number: '',
                                        position: '',
                                        dob: '',
                                      });
                                    }}
                                    className="btn-cancel-player"
                                  >
                                    ✕
                                  </button>
                                </td>
                              </tr>
                            )}

                            {roster.map((player) =>
                              editingPlayerId === player.id ? (
                                <tr key={player.id} className="player-form-row">
                                  <td>
                                    <input
                                      type="text"
                                      value={editPlayer.number}
                                      onChange={(e) =>
                                        setEditPlayer({
                                          ...editPlayer,
                                          number: e.target.value,
                                        })
                                      }
                                      className="player-input"
                                      onKeyDown={(e) => {
                                        if (e.key === 'Enter') {
                                          e.preventDefault();
                                          handleEditPlayer(player.id);
                                        }
                                      }}
                                    />
                                  </td>
                                  <td>
                                    <input
                                      type="text"
                                      value={editPlayer.name}
                                      onChange={(e) =>
                                        setEditPlayer({
                                          ...editPlayer,
                                          name: e.target.value,
                                        })
                                      }
                                      className="player-input"
                                      required
                                      onKeyDown={(e) => {
                                        if (e.key === 'Enter') {
                                          e.preventDefault();
                                          handleEditPlayer(player.id);
                                        }
                                      }}
                                    />
                                  </td>
                                  <td>
                                    <input
                                      type="text"
                                      value={editPlayer.position}
                                      onChange={(e) =>
                                        setEditPlayer({
                                          ...editPlayer,
                                          position: e.target.value,
                                        })
                                      }
                                      className="player-input"
                                      onKeyDown={(e) => {
                                        if (e.key === 'Enter') {
                                          e.preventDefault();
                                          handleEditPlayer(player.id);
                                        }
                                      }}
                                    />
                                  </td>
                                  <td>
                                    <input
                                      type="text"
                                      value={editPlayer.dob}
                                      onChange={(e) =>
                                        setEditPlayer({
                                          ...editPlayer,
                                          dob: e.target.value,
                                        })
                                      }
                                      placeholder="MM/DD/YYYY"
                                      className="player-input"
                                      onKeyDown={(e) => {
                                        if (e.key === 'Enter') {
                                          e.preventDefault();
                                          handleEditPlayer(player.id);
                                        }
                                      }}
                                      style={{
                                        borderColor:
                                          editPlayer.dob &&
                                          new Date(
                                            editPlayer.dob
                                          ).toString() === 'Invalid Date'
                                            ? '#ef4444'
                                            : '',
                                      }}
                                    />
                                    {editPlayer.dob &&
                                      (new Date(editPlayer.dob).toString() !==
                                      'Invalid Date' ? (
                                        <div
                                          style={{
                                            fontSize: '10px',
                                            color: '#10b981',
                                            marginTop: '2px',
                                          }}
                                        >
                                          ✓{' '}
                                          {new Date(
                                            editPlayer.dob
                                          ).toLocaleDateString()}
                                        </div>
                                      ) : (
                                        <div
                                          style={{
                                            fontSize: '10px',
                                            color: '#ef4444',
                                            marginTop: '2px',
                                          }}
                                        >
                                          ✗ Invalid date format
                                        </div>
                                      ))}
                                  </td>
                                  <td>
                                    <button
                                      type="button"
                                      onClick={() =>
                                        handleEditPlayer(player.id)
                                      }
                                      className="btn-save-player"
                                    >
                                      ✓
                                    </button>
                                    <button
                                      type="button"
                                      onClick={() => setEditingPlayerId(null)}
                                      className="btn-cancel-player"
                                    >
                                      ✕
                                    </button>
                                  </td>
                                </tr>
                              ) : (
                                <tr key={player.id}>
                                  <td>{player.number || '-'}</td>
                                  <td>{player.name}</td>
                                  <td>{player.position || '-'}</td>
                                  <td>
                                    {player.dob
                                      ? (() => {
                                          const [year, month, day] =
                                            player.dob.split('-');
                                          return `${month}/${day}/${year}`;
                                        })()
                                      : '-'}
                                  </td>
                                  <td>
                                    <button
                                      type="button"
                                      onClick={() => startEditingPlayer(player)}
                                      className="btn-edit-player"
                                    >
                                      ✏️
                                    </button>
                                    <button
                                      type="button"
                                      onClick={() =>
                                        setShowDeletePlayerConfirm(player.id)
                                      }
                                      className="btn-delete-player"
                                    >
                                      🗑️
                                    </button>
                                  </td>
                                </tr>
                              )
                            )}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                )
              )}
            </div>
            {/* Form Actions - EXISTING CODE UNCHANGED */}
            <div className="form-actions">
              {isTeamOwner ? (
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={saving || deleting}
                >
                  {saving ? 'Saving Changes...' : 'Save Changes'}
                </button>
              ) : (
                <>
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={() => navigate(`/team/${id}`)}
                    disabled={saving}
                  >
                    ← Back to Team Portal
                  </button>
                </>
              )}

              {isTeamOwner && (
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={() => setShowDeleteConfirm(true)}
                  disabled={saving || deleting}
                >
                  Delete Team
                </button>
              )}
            </div>
          </form>

          {/* Delete Team Confirmation Modal */}
          {showDeleteConfirm && (
            <div
              className="modal-overlay"
              onClick={() => setShowDeleteConfirm(false)}
            >
              <div
                className="modal-content"
                onClick={(e) => e.stopPropagation()}
              >
                <h3>Delete Team?</h3>
                <p>
                  <strong>{formData.name}</strong> will be moved to{' '}
                  <strong>Recently Deleted</strong> for 3 days in your profile.
                  You can restore it from your Account Settings during this
                  time.
                </p>
                <div className="modal-actions">
                  <button
                    className="btn btn-primary"
                    onClick={() => setShowDeleteConfirm(false)}
                    disabled={deleting}
                  >
                    Cancel
                  </button>
                  <button
                    className="btn btn-primary"
                    onClick={handleDelete}
                    disabled={deleting}
                  >
                    {deleting ? 'Deleting...' : 'Move to Trash'}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Delete Player Confirmation Modal */}
          {showDeletePlayerConfirm && (
            <div
              className="modal-overlay"
              onClick={() => setShowDeletePlayerConfirm(null)}
            >
              <div
                className="modal-content"
                onClick={(e) => e.stopPropagation()}
              >
                <h3>Delete Player?</h3>
                <p>
                  Are you sure you want to remove this player from the roster?
                </p>
                <div className="modal-actions">
                  <button
                    className="btn-cancel"
                    onClick={() => setShowDeletePlayerConfirm(null)}
                  >
                    Cancel
                  </button>
                  <button
                    className="btn-confirm-delete"
                    onClick={() => handleDeletePlayer(showDeletePlayerConfirm)}
                  >
                    Yes, Delete
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* CSV/Excel Import Modal */}
          {showCSVModal && (
            <div
              className="modal-overlay"
              onClick={() => setShowCSVModal(false)}
            >
              <div
                className="csv-modal-content"
                onClick={(e) => e.stopPropagation()}
              >
                <h3>📄 Import Roster from File</h3>

                {!csvFile ? (
                  <div className="csv-upload-area">
                    <input
                      type="file"
                      accept=".csv,.xlsx,.xls"
                      onChange={handleFileUpload}
                      id="csv-upload"
                      style={{ display: 'none' }}
                    />
                    <label
                      htmlFor="csv-upload"
                      className={`csv-upload-label ${
                        dragActive ? 'drag-active' : ''
                      }`}
                      onDragEnter={handleDrag}
                      onDragLeave={handleDrag}
                      onDragOver={handleDrag}
                      onDrop={handleDrop}
                    >
                      <span className="upload-icon">📁</span>
                      <p>Click to upload CSV or Excel file</p>
                      <p className="upload-hint">or drag and drop</p>
                    </label>
                  </div>
                ) : (
                  <div className="csv-mapping">
                    <p className="csv-info">
                      Found {csvData.length} rows in {csvFile.name}. Map your
                      columns:
                    </p>

                    <div className="mapping-grid">
                      <div className="mapping-row">
                        <label>Name * (required)</label>
                        <select
                          value={csvMapping.name}
                          onChange={(e) =>
                            setCsvMapping({
                              ...csvMapping,
                              name: e.target.value,
                            })
                          }
                        >
                          <option value="">Select column...</option>
                          {csvHeaders.map((header) => (
                            <option key={header} value={header}>
                              {header}
                            </option>
                          ))}
                        </select>
                      </div>

                      <div className="mapping-row">
                        <label>Number (optional)</label>
                        <select
                          value={csvMapping.number}
                          onChange={(e) =>
                            setCsvMapping({
                              ...csvMapping,
                              number: e.target.value,
                            })
                          }
                        >
                          <option value="">Select column...</option>
                          {csvHeaders.map((header) => (
                            <option key={header} value={header}>
                              {header}
                            </option>
                          ))}
                        </select>
                      </div>

                      <div className="mapping-row">
                        <label>Position (optional)</label>
                        <select
                          value={csvMapping.position}
                          onChange={(e) =>
                            setCsvMapping({
                              ...csvMapping,
                              position: e.target.value,
                            })
                          }
                        >
                          <option value="">Select column...</option>
                          {csvHeaders.map((header) => (
                            <option key={header} value={header}>
                              {header}
                            </option>
                          ))}
                        </select>
                      </div>

                      <div className="mapping-row">
                        <label>Date of Birth (optional)</label>
                        <select
                          value={csvMapping.dob}
                          onChange={(e) =>
                            setCsvMapping({
                              ...csvMapping,
                              dob: e.target.value,
                            })
                          }
                        >
                          <option value="">Select column...</option>
                          {csvHeaders.map((header) => (
                            <option key={header} value={header}>
                              {header}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>

                    {csvMapping.name && csvData.length > 0 && (
                      <div className="csv-preview">
                        <p className="preview-title">Preview first 3 rows:</p>
                        <table className="preview-table">
                          <thead>
                            <tr>
                              <th>Name</th>
                              <th>Number</th>
                              <th>Position</th>
                              <th>DOB</th>
                            </tr>
                          </thead>
                          <tbody>
                            {csvData.slice(0, 3).map((row, idx) => (
                              <tr key={idx}>
                                <td>{row[csvMapping.name] || '-'}</td>
                                <td>{row[csvMapping.number] || '-'}</td>
                                <td>{row[csvMapping.position] || '-'}</td>
                                <td>{row[csvMapping.dob] || '-'}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                )}

                <div className="modal-actions">
                  <button
                    className="btn-cancel"
                    onClick={() => {
                      setShowCSVModal(false);
                      setCsvFile(null);
                      setCsvData([]);
                      setCsvHeaders([]);
                      setCsvMapping({
                        name: '',
                        number: '',
                        position: '',
                        dob: '',
                      });
                      setError('');
                    }}
                    disabled={importing}
                  >
                    Cancel
                  </button>
                  {csvFile && (
                    <button
                      className="btn btn-primary"
                      onClick={handleCSVImport}
                      disabled={!csvMapping.name || importing}
                    >
                      {importing
                        ? 'Importing...'
                        : `Import ${csvData.length} Players`}
                    </button>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
        {/* Toast Notifications */}
        {toastMessage.text && toastMessage.type === 'success' && (
          <div className="toast-message toast-success">{toastMessage.text}</div>
        )}
        {toastMessage.text && toastMessage.type === 'error' && (
          <div className="toast-message toast-error">{toastMessage.text}</div>
        )}
      </div>
      <Footer />
    </>
  );
}

export default EditTeam;
